/**
 * School Admin Dashboard - Main Container (Dual-Mode: Session + UUID)
 *
 * Purpose: Manage school-wide institutional license, student roster, teacher adoption
 * Routes:
 *  - School Admin: /admin/school/* (session-based, no ID in URL)
 *  - District Admin: /admin/schools/:schoolUuid/* (UUID-based, secure)
 *
 * Features:
 * - License overview & seat management
 * - Student roster with engagement tracking
 * - Teacher adoption metrics
 * - Learning analytics & ROI
 * - Content moderation oversight
 *
 * Architecture (2025-11-14 UUID-Based Security):
 * - School admins: Session-based API calls /api/admin/school/* (most secure)
 * - District admins: UUID-based API calls /api/admin/schools/{uuid}/* (non-enumerable)
 * - Auto-detects mode based on URL params
 * - Prevents enumeration attacks with UUIDs
 *
 * References:
 * - /docs/ADMIN_DASHBOARDS_IMPLEMENTATION.md
 * - /docs/ADMIN_DASHBOARD_SCALING_ARCHITECTURE.md
 * - Backend: /web_ui/api/admin_api.py
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from '../utils/authInterceptor';
import './morpheus-interface.css'; // Reuse teacher dashboard styles

// Tab components
import SchoolAdminOverview from './SchoolAdminOverview';
import SchoolLicenseManagement from './SchoolLicenseManagement';
import SchoolStudentRoster from './SchoolStudentRoster';
import SchoolTeacherManagement from './SchoolTeacherManagement';
import SchoolAnalytics from './SchoolAnalytics';
import SchoolSafetyDashboard from './SchoolSafetyDashboard';
import ExportModal from './ExportModal';

// Official Tappy Brand Colors (from /morpheus-rl-marketing/tappy_brand_colors.md)
const TappyBrandColors = {
  primary: {
    darkTeal: '#1A3E48',
    teal: '#297A92',
    mediumTeal: '#388899',
    brandBlue: '#76909F',
    brightTeal: '#42909E',
    aqua: '#4D99A5',
    lightAqua: '#5BA3AC',
    skyBlue: '#6DB4CD',
  },
  accent: {
    coral: '#EA6D37',
    gold: '#ECC962',
    brightGold: '#F8C341',
    tan: '#C59F65',
  },
  neutral: {
    darkSlate: '#3D4954',
    deepPurple: '#391339',
    mauve: '#8D7F88',
    mintGray: '#ABCACA',
    lightGray: '#D5DEDD',
    paleGray: '#ECF3F2',
    offWhite: '#F5F9FA',
    nearWhite: '#FCFDFE',
  },
  semantic: {
    success: '#6DB4CD',
    warning: '#ECC962',
    error: '#EA6D37',
    info: '#42909E',
    primary: '#297A92',
    secondary: '#76909F',
  }
};

function SchoolAdminDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { schoolId: schoolUuidFromUrl } = useParams();

  // Determine mode: UUID-based (district admin) or session-based (school admin)
  const isDistrictAdminView = !!schoolUuidFromUrl;
  const schoolUuid = schoolUuidFromUrl; // UUID from URL for district admins
  const schoolId = user?.school_license_id; // Session ID for school admins

  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);

  // Dashboard data
  const [overviewData, setOverviewData] = useState(null);
  const [licenseData, setLicenseData] = useState(null);
  const [studentsData, setStudentsData] = useState(null);

  // Parse current tab from URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/overview')) setActiveTab('overview');
    else if (path.includes('/license')) setActiveTab('license');
    else if (path.includes('/students')) setActiveTab('students');
    else if (path.includes('/teachers')) setActiveTab('teachers');
    else if (path.includes('/analytics')) setActiveTab('analytics');
    else if (path.includes('/safety')) setActiveTab('safety');
    else setActiveTab('overview'); // Default
  }, [location]);

  // Helper to build API URL based on mode
  const getApiUrl = (endpoint) => {
    if (isDistrictAdminView) {
      // UUID-based routing for district admins
      return `/api/admin/schools/${schoolUuid}/${endpoint}`;
    } else {
      // Session-based routing for school admins
      return `/api/admin/school/${endpoint}`;
    }
  };

  // Helper to build navigation URL based on mode
  const getNavUrl = (tab) => {
    if (isDistrictAdminView) {
      return `/admin/schools/${schoolUuid}/${tab}`;
    } else {
      return `/admin/school/${tab}`;
    }
  };

  // Fetch overview data on mount
  useEffect(() => {
    if (schoolId || schoolUuid) {
      fetchOverviewData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, schoolUuid]);

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl('overview'));
      setOverviewData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching overview data:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLicenseData = async () => {
    try {
      const response = await axios.get(getApiUrl('license'));
      setLicenseData(response.data);
    } catch (err) {
      console.error('Error fetching license data:', err);
    }
  };

  const fetchStudentsData = async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters);
      const response = await axios.get(`${getApiUrl('students')}?${params}`);
      setStudentsData(response.data);
    } catch (err) {
      console.error('Error fetching students data:', err);
    }
  };

  // Navigate to tab
  const navigateToTab = (tab) => {
    setActiveTab(tab);
    navigate(getNavUrl(tab));

    // Fetch data for tab if not already loaded
    if (tab === 'license' && !licenseData) {
      fetchLicenseData();
    } else if (tab === 'students' && !studentsData) {
      fetchStudentsData();
    }
  };

  // Tab configuration - Clean iOS-style without emojis
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'license', label: 'License' },
    { id: 'students', label: 'Students' },
    { id: 'teachers', label: 'Teachers' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'safety', label: 'Safety' }
  ];

  // Loading state - Notion-style skeleton
  if (loading) {
    return (
      <div className="morpheus-interface" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: TappyBrandColors.neutral.offWhite
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            margin: '0 auto 24px',
            borderRadius: '16px',
            backgroundColor: TappyBrandColors.neutral.paleGray,
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
            border: `2px solid ${TappyBrandColors.neutral.mintGray}`
          }} />
          <p style={{
            fontSize: '16px',
            color: TappyBrandColors.primary.brandBlue,
            fontWeight: 500,
            letterSpacing: '-0.2px'
          }}>Loading School Dashboard...</p>
          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Error state - Clean iOS-style
  if (error) {
    return (
      <div className="morpheus-interface" style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div style={{
            width: '100px',
            height: '100px',
            margin: '0 auto 24px',
            borderRadius: '20px',
            backgroundColor: TappyBrandColors.neutral.paleGray,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `3px solid ${TappyBrandColors.accent.coral}`
          }}>
            <div style={{
              fontSize: '48px',
              fontWeight: 700,
              color: TappyBrandColors.accent.coral
            }}>!</div>
          </div>
          <h2 style={{
            color: TappyBrandColors.accent.coral,
            marginBottom: '20px',
            fontSize: '24px',
            fontWeight: 600,
            letterSpacing: '-0.3px'
          }}>Dashboard Error</h2>
          <p style={{
            fontSize: '16px',
            color: TappyBrandColors.neutral.darkSlate,
            marginBottom: '30px',
            lineHeight: '1.6'
          }}>{error}</p>
          <button
            onClick={fetchOverviewData}
            style={{
              padding: '12px 24px',
              backgroundColor: TappyBrandColors.primary.teal,
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              boxShadow: `0 2px 8px rgba(41, 122, 146, 0.25)`
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.primary.brightTeal;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.primary.teal;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Retry
          </button>
          <button
            onClick={onLogout}
            style={{
              padding: '12px 24px',
              marginLeft: '10px',
              backgroundColor: TappyBrandColors.neutral.lightGray,
              color: TappyBrandColors.neutral.darkSlate,
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              transition: 'all 200ms ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.mintGray;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.lightGray;
            }}
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="morpheus-interface">
      {/* Back Button - Only for District Admin View */}
      {isDistrictAdminView && (
        <div style={{
          padding: '16px 40px',
          backgroundColor: TappyBrandColors.neutral.offWhite,
          borderBottom: `1px solid ${TappyBrandColors.neutral.lightGray}`
        }}>
          <button
            onClick={() => navigate('/admin/district')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: 'white',
              color: TappyBrandColors.primary.teal,
              border: `1px solid ${TappyBrandColors.neutral.mintGray}`,
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 200ms ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.paleGray;
              e.currentTarget.style.borderColor = TappyBrandColors.primary.teal;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = TappyBrandColors.neutral.mintGray;
            }}
          >
            <span style={{ fontSize: '16px' }}>←</span>
            Back to All Schools
          </button>
        </div>
      )}

      {/* Header - iOS-style Glassmorphism with Logo */}
      <div className="morpheus-header" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 40px',
        backgroundColor: `rgba(252, 253, 254, 0.85)`,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: `1px solid ${TappyBrandColors.neutral.lightGray}`,
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: `0 1px 3px rgba(26, 62, 72, 0.08)`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <img
            src="/images/TAPPY-logo-black.png"
            alt="Teach Tappy Logo"
            style={{
              height: '32px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
          <div>
            <h1 style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: 600,
              color: TappyBrandColors.primary.darkTeal,
              letterSpacing: '-0.5px'
            }}>
              {overviewData?.school?.name || 'School Dashboard'}
            </h1>
            <p style={{
              margin: '4px 0 0 0',
              fontSize: '14px',
              color: TappyBrandColors.primary.brandBlue
            }}>
              School Administrator · {user?.email}
            </p>
          </div>
        </div>
        <button
          onClick={onLogout}
          style={{
            padding: '10px 20px',
            backgroundColor: TappyBrandColors.neutral.paleGray,
            color: TappyBrandColors.neutral.darkSlate,
            border: `1px solid ${TappyBrandColors.neutral.lightGray}`,
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            transition: 'all 200ms ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.lightGray;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.paleGray;
          }}
        >
          Logout
        </button>
      </div>

      {/* Tab Navigation - Clean iOS-style */}
      <div className="morpheus-tabs" style={{
        display: 'flex',
        gap: '4px',
        padding: '0 40px',
        backgroundColor: '#ffffff',
        borderBottom: `1px solid ${TappyBrandColors.neutral.lightGray}`
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => navigateToTab(tab.id)}
            className={activeTab === tab.id ? 'active' : ''}
            style={{
              padding: '14px 24px',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? `3px solid ${TappyBrandColors.primary.teal}` : '3px solid transparent',
              color: activeTab === tab.id ? TappyBrandColors.primary.teal : TappyBrandColors.primary.brandBlue,
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === tab.id ? 600 : 500,
              transition: 'all 200ms ease',
              letterSpacing: '-0.2px'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = TappyBrandColors.primary.darkTeal;
                e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.paleGray;
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = TappyBrandColors.primary.brandBlue;
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="morpheus-content" style={{
        padding: '40px',
        backgroundColor: TappyBrandColors.neutral.offWhite,
        minHeight: 'calc(100vh - 140px)'
      }}>
        {activeTab === 'overview' && (
          <SchoolAdminOverview
            data={overviewData}
            onRefresh={fetchOverviewData}
            onNavigateToTab={navigateToTab}
            onOpenExport={() => setShowExportModal(true)}
          />
        )}

        {activeTab === 'license' && (
          <SchoolLicenseManagement
            getApiUrl={getApiUrl}
            isDistrictAdminView={isDistrictAdminView}
          />
        )}

        {activeTab === 'students' && (
          <SchoolStudentRoster
            getApiUrl={getApiUrl}
            isDistrictAdminView={isDistrictAdminView}
          />
        )}

        {activeTab === 'teachers' && (
          <SchoolTeacherManagement
            getApiUrl={getApiUrl}
            isDistrictAdminView={isDistrictAdminView}
          />
        )}

        {activeTab === 'analytics' && (
          <SchoolAnalytics
            getApiUrl={getApiUrl}
            isDistrictAdminView={isDistrictAdminView}
          />
        )}

        {activeTab === 'safety' && (
          <SchoolSafetyDashboard
            getApiUrl={getApiUrl}
            isDistrictAdminView={isDistrictAdminView}
          />
        )}

        {/* Teach Tappy Footer - Consistent with TutorInterface */}
        <div style={{
          marginTop: '60px',
          backgroundColor: 'rgba(245, 249, 250, 0.95)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(213, 222, 221, 0.5)',
          padding: '1rem',
          textAlign: 'center',
          fontSize: '0.65rem',
          color: '#76909f'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.3rem',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <img
              src="/images/TAPPY-logo-black.png"
              alt="Tappy Logo"
              style={{
                height: '18px',
                width: 'auto',
                objectFit: 'contain',
                marginBottom: '0.2rem'
              }}
            />
            <p style={{ margin: 0, fontSize: '0.65rem', lineHeight: '1.4' }}>
              &copy; {new Date().getFullYear()} Teach Tappy | Powered by <span style={{ fontWeight: 500 }}>Morpheus RL</span>
            </p>
            <p style={{ margin: 0, fontSize: '0.6rem', lineHeight: '1.4' }}>
              By using our services, you agree to Tappy's{' '}
              <a
                href="/terms-of-service.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#76909f',
                  textDecoration: 'underline'
                }}
              >
                Terms of Use
              </a>
              {' '}and{' '}
              <a
                href="/privacy-policy.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#76909f',
                  textDecoration: 'underline'
                }}
              >
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        exportType="school"
        schoolData={overviewData}
      />
    </div>
  );
}

export default SchoolAdminDashboard;
